import { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/Table';
import {
  Interaction,
  InteractionType,
  FeedbackStatus,
  FeedbackInteraction,
  AllGoalTypes,
} from '@momentum/types';
import { useTranslation } from 'react-i18next';
import { InteractionRow } from './InteractionRow';
import {
  getParticipantNames,
  getReasonSelection,
} from '../utils/interaction.utils';
import type {
  FilterState,
  EmployeeMap,
} from '../types/interaction-table.types';

interface InteractionsTableTabProps {
  interactions: Interaction[];
  filters: FilterState;
  currentEmployeeId: string;
  employeeMap: EmployeeMap;
  goals: AllGoalTypes[];
  isLoading?: boolean;
  onRowClick: (interaction: Interaction) => void;
}

export const InteractionsTableTab = ({
  interactions,
  filters,
  currentEmployeeId,
  employeeMap,
  goals,
  isLoading,
  onRowClick,
}: InteractionsTableTabProps) => {
  const { t } = useTranslation('interactions');

  const shouldShowFeedbackAlert = (interaction: Interaction): boolean => {
    if (interaction.type !== InteractionType.FEEDBACK) return false;
    const feedback = interaction as FeedbackInteraction;
    return (
      feedback.receiverId === currentEmployeeId &&
      feedback.status !== FeedbackStatus.FINISHED
    );
  };

  const filteredInteractions = useMemo(() => {
    return interactions.filter((interaction) => {
      // Type filter
      if (filters.type !== 'all' && interaction.type !== filters.type) {
        return false;
      }

      // Status filter (only for feedback)
      if (filters.status !== 'all') {
        if (
          !('status' in interaction) ||
          interaction.status !== filters.status
        ) {
          return false;
        }
      }

      // Date filters
      if (filters.dateFrom) {
        const interactionDate = new Date(interaction.metadata.createdAt);
        if (interactionDate < new Date(filters.dateFrom)) {
          return false;
        }
      }
      if (filters.dateTo) {
        const interactionDate = new Date(interaction.metadata.createdAt);
        if (interactionDate > new Date(filters.dateTo)) {
          return false;
        }
      }

      // Search filter (search in participant names)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const participants = getParticipantNames(
          interaction,
          currentEmployeeId,
          employeeMap,
          t('unknown')
        );
        return participants.some((name) =>
          name.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [interactions, filters, currentEmployeeId, employeeMap, t]);

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell
            colSpan={6}
            className="text-center py-8 text-muted-foreground"
          >
            {t('loadingInteractions')}
          </TableCell>
        </TableRow>
      );
    }

    if (filteredInteractions.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center py-16">
            <div className="flex flex-col items-center justify-center space-y-4">
              <img
                src="/undraw-illustrations/undraw_no-data_ig65.svg"
                alt={t('noData')}
                className="w-48 h-48 opacity-50"
              />
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium">
                  {t('noInteractionsFound')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('tryAdjustingFilters')}
                </p>
              </div>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return filteredInteractions.map((interaction) => {
      const participants = getParticipantNames(
        interaction,
        currentEmployeeId,
        employeeMap,
        t('unknown')
      );
      const reason = getReasonSelection(interaction);
      const showAlert = shouldShowFeedbackAlert(interaction);

      return (
        <InteractionRow
          key={interaction._id}
          interaction={interaction}
          participants={participants}
          reason={reason}
          showAlert={showAlert}
          goals={goals}
          onClick={onRowClick}
        />
      );
    });
  };

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('type')}
            </TableHead>
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('participants')}
            </TableHead>
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('date')}
            </TableHead>
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('status')}
            </TableHead>
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('goal')}
            </TableHead>
            <TableHead className="font-semibold text-xs uppercase tracking-wide">
              {t('reason')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderTableContent()}</TableBody>
      </Table>
    </div>
  );
};
